import React, { useCallback, useEffect, useState } from "react";
import { Users as AiFillContacts, DollarSign as AiFillDollarCircle, Eye as AiFillEye, ShoppingBag as AiFillShopping, ArrowDown as AiOutlineArrowDown, ArrowUp as AiOutlineArrowUp, RefreshCw, Info } from "lucide-react";

const cards = [
  { key: "views", label: "Total Views", icon: AiFillEye, accent: "bg-sky-50 text-sky-600 ring-sky-100", comparison: "Views vs last month", helper: "Page views since tracking began; staff pages excluded." },
  { key: "revenue", label: "Total Revenue", icon: AiFillDollarCircle, accent: "bg-emerald-50 text-emerald-600 ring-emerald-100", comparison: "Revenue vs last month", helper: "Successful USD payments, before refunds and expenses." },
  { key: "sales", label: "Sales", icon: AiFillShopping, accent: "bg-amber-50 text-amber-600 ring-amber-100", comparison: "Sales vs last month", helper: "Successful paid transactions, including subscription renewals." },
  { key: "users", label: "Total Users", icon: AiFillContacts, accent: "bg-indigo-50 text-indigo-600 ring-indigo-100", comparison: "New users vs last month", helper: "All current accounts, including staff and unverified users." },
];

const formatValue = (value, currency) => new Intl.NumberFormat("en-US", currency
  ? { style: "currency", currency, maximumFractionDigits: 2 }
  : { maximumFractionDigits: 0 }).format(value);

function StatCard({ card, metric, loading }) {
  const Icon = card.icon;
  const change = metric?.changePercent;
  const positive = typeof change === "number" && change > 0;
  const negative = typeof change === "number" && change < 0;
  const comparison = !metric ? "Unavailable" : !metric.comparisonAvailable ? "Tracking is new"
    : change === null ? "No prior baseline" : `${Math.abs(change).toFixed(1)}%`;
  return (
    <article className={`admin-card admin-stat-card stat-${card.key}`}>
      <div className="admin-stat-heading"><span className="admin-stat-icon"><Icon size={19} strokeWidth={1.7} /></span><p>{card.label}</p><span className="admin-stat-info" title={card.helper} aria-label={card.helper} tabIndex={0}><Info size={14} /></span></div>
      <h3 className={`admin-stat-value ${loading ? "admin-skeleton" : ""} ${!metric && !loading ? "is-unavailable" : ""}`}>
        {loading ? <span className="sr-only">Loading {card.label}</span> : metric ? formatValue(metric.total, metric.currency) : "Unavailable"}
      </h3>
      <div className="admin-stat-comparison"
        title={metric ? `This month to date: ${formatValue(metric.current, metric.currency)}; previous full month: ${formatValue(metric.previous, metric.currency)} (UTC).` : undefined}>
        <span className="admin-stat-comparison-label">{card.comparison}</span>
        <span aria-label={positive ? `Up ${change}%` : negative ? `Down ${Math.abs(change)}%` : undefined}
          className={`admin-stat-change ${!loading && positive ? "positive" : !loading && negative ? "negative" : "neutral"}`}>
          {!loading && positive && <AiOutlineArrowUp size={12} />}
          {!loading && negative && <AiOutlineArrowDown size={12} />}
          {loading ? "..." : comparison}
        </span>
      </div>
    </article>
  );
}

export default function OverviewStats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await fetch("/api/admin/overview", { credentials: "same-origin", cache: "no-store", signal: controller.signal });
        if (!response.ok) throw new Error(response.status === 403 || response.status === 401
          ? "Sign in with an admin account to view dashboard totals." : "Unable to load dashboard totals. Please retry.");
        const result = await response.json();
        if (cards.some(({ key }) => !Number.isFinite(result[key]?.total))) throw new Error("Dashboard totals are unavailable. Please retry.");
        if (!controller.signal.aborted) setData(result);
      } catch (loadError) {
        if (!controller.signal.aborted) {
          setData(null);
          setError(loadError.message || "Unable to load dashboard totals. Please retry.");
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [revision]);

  useEffect(() => {
    const interval = setInterval(() => { if (!document.hidden) refresh(); }, 60000);
    return () => clearInterval(interval);
  }, [refresh]);

  return (
    <section className="admin-overview" aria-label="Dashboard totals" aria-busy={loading}>
      <div className="admin-section-heading">
        <h2>Platform performance <span>All time</span></h2>
        <button type="button" onClick={refresh} disabled={loading}
          className="admin-refresh"><RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          {loading ? "Refreshing..." : "Refresh totals"}
        </button>
      </div>
      {error && <p role="alert" className="admin-alert">{error}</p>}
      <div className="admin-stats-grid">
        {cards.map((card) => <StatCard key={card.key} card={card} metric={data?.[card.key]} loading={loading} />)}
      </div>
      <p className="admin-metric-note">Changes compare this month to date with the previous full month (UTC).</p>
    </section>
  );
}
